import { useState, useEffect, useRef } from 'react';
import { Activity } from 'lucide-react';
import axios from 'axios';
import { TradeDetailModal } from './TradeDetailModal';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

const DEFAULT_FEE_RATE = 0.0125; // 1.25% per side (2.5% round-trip floor)

const LiveTradeFeed = ({ mode }) => {
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState({ buy_count: 0, sell_count: 0 });
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [feeRate, setFeeRate] = useState(DEFAULT_FEE_RATE);
  const scrollRef = useRef(null);

  // Fetch user's dynamic fee rate
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_V2}/user/fee-info`, { withCredentials: true });
        if (res.data?.taker_fee) setFeeRate(res.data.taker_fee);
      } catch { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const params = { limit: 50 };
        if (mode) params.mode = mode;
        const res = await axios.get(`${API_V2}/trades/feed`, { params, withCredentials: true });
        setTrades(res.data?.trades || []);
        setStats({ buy_count: res.data?.buy_count || 0, sell_count: res.data?.sell_count || 0 });
      } catch { /* noop */ }
    };
    fetchTrades();
    const id = setInterval(fetchTrades, 5000);
    return () => clearInterval(id);
  }, [mode]);

  if (!trades.length) return null;

  return (
    <>
      <div className="mb-4 border border-[#003B00] bg-[#050505]" data-testid="live-trade-feed">
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[#003B00] bg-[#0a0a0a]">
          <Activity className="h-3 w-3 text-[#00FF41] animate-pulse" />
          <span className="text-[10px] text-[#00FF41] uppercase tracking-widest font-bold">Live Trade Feed</span>
          <span className="text-[9px] text-gray-600 ml-auto">
            {stats.buy_count}B / {stats.sell_count}S — {trades.length} trades ({mode || 'all'})
          </span>
        </div>
        <div ref={scrollRef} className="overflow-y-auto max-h-[320px] divide-y divide-[#003B00]/30">
          {trades.map((t, i) => {
            const isBuy = (t.type || '').toUpperCase() === 'BUY';
            const isLive = t.mode === 'live';
            const coin = (t.pair || '').split('-')[0];
            const pl = t.profit != null ? Number(t.profit) : null;
            const ts = t.timestamp ? new Date(t.timestamp) : null;
            const timeStr = ts ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
            const dateStr = ts ? ts.toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';

            return (
              <div 
                key={i} 
                className="flex items-center gap-3 px-3 py-1.5 text-[11px] hover:bg-[#001a00] transition-colors cursor-pointer"
                onClick={() => setSelectedTrade(t)}
                data-testid={`trade-feed-row-${i}`}
              >
                <span className="text-gray-600 w-[72px] shrink-0 text-[10px]">{dateStr} {timeStr}</span>
                <span className="text-gray-400 w-[70px] shrink-0 truncate">{t.user}</span>
                <span className={`w-[36px] shrink-0 text-center text-[9px] font-bold px-1 py-0.5 ${
                  isBuy ? 'text-green-400 bg-green-900/30 border border-green-800/50' : 'text-red-400 bg-red-900/30 border border-red-800/50'
                }`}>
                  {isBuy ? 'BUY' : 'SELL'}
                </span>
                <span className="text-[#00FF41] font-bold w-[42px] shrink-0">{coin}</span>
                <span className="text-gray-400 w-[80px] shrink-0 text-right">
                  ${Number(t.usd_value || 0).toFixed(2)}
                </span>
                <span className={`w-[36px] shrink-0 text-center text-[8px] font-bold px-1 py-0.5 uppercase ${
                  isLive ? 'text-orange-400 border border-orange-800/50' : 'text-blue-400 border border-blue-800/50'
                }`}>
                  {isLive ? 'LIVE' : 'SIM'}
                </span>
                <span className={`w-[70px] shrink-0 text-right font-mono ${
                  pl == null ? 'text-gray-600' : pl >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {pl != null ? `${pl >= 0 ? '+' : ''}$${pl.toFixed(2)}` : '--'}
                </span>
                <span className="text-gray-600 text-[9px] truncate">{t.trigger || ''}</span>
              </div>
            );
          })}
        </div>
      </div>

      {selectedTrade && (
        <TradeDetailModal trade={selectedTrade} onClose={() => setSelectedTrade(null)} feeRate={feeRate} />
      )}
    </>
  );
};

export default LiveTradeFeed;
